import { useCallback, useContext, useEffect, useState } from 'react'
import { getFlightFareFamilies, searchFlights } from 'api/flights'
import { fareClasses, FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, FlightsFareTypes, FlightViewTypes } from 'constants/flight'
import GeoContext from 'contexts/geoContext'
import { last, nonNullable } from 'lib/array/arrayUtils'
import moment from 'moment'
import { EmptyBaggage, getJourneyFields } from 'lib/checkout/flights/cart'
import { useAppDispatch } from 'hooks/reduxHooks'
import { userCartItemsSaveForLater, userCartItemUpdate } from 'actions/AuthActions'
import useTabVisibility from 'hooks/useTabVisibility'
import { multiCartRefreshFlightItemEvent } from 'analytics/snowplow/events'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

type FlightDataStatus = 'valid' | 'expired' | 'refreshing' | 'refreshFailed'

function isFlightExpired(expiry: number | undefined) {
  return !!expiry && Date.now() >= expiry
}

function useRefreshFlightItem(item: App.Checkout.FlightItem): {
  dataStatus: FlightDataStatus
  initiateRefresh: () => void
  refreshStartedAt: number
} {
  const { expiry } = item
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)

  const dispatch = useAppDispatch()
  const [dataStatus, setStatus] = useState<FlightDataStatus>(() => isFlightExpired(expiry) ? 'expired' : 'valid')
  const [refreshStartedAt, setRefreshStartedAt] = useState(0)

  const tabIsVisible = useTabVisibility()

  const enableSabreContent = useOptimizelyExperiment(OptimizelyFeatureFlags.flightsEnableSabreContent)

  useEffect(() => {
    if (expiry && dataStatus === 'valid' && tabIsVisible) {
      const millisecondsUntilExpiry = expiry - Date.now()

      if (millisecondsUntilExpiry > 0) {
        const timeout = setTimeout(() => {
          setStatus('expired')
        }, millisecondsUntilExpiry)
        return () => clearTimeout(timeout)
      } else {
        setStatus('expired')
      }
    }
  }, [expiry, dataStatus, tabIsVisible])

  const initiateRefresh = useCallback(async() => {
    const { flights, occupants, forceBundleId } = item

    const isStandalone = !item.bundledItemIds?.length

    if (!flights?.length || !occupants) {
      return
    }

    multiCartRefreshFlightItemEvent(item)
    setStatus('refreshing')
    setRefreshStartedAt(Date.now())

    try {
      let fareType: string
      if (flights.length === 1) {
        fareType = FlightsFareTypes.ONE_WAY
      } else if (flights.length === 2 && flights[0].arrivalAirportCode === flights[1].departingAirportCode && flights[0].departingAirportCode === flights[1].arrivalAirportCode) {
        fareType = FlightsFareTypes.RETURN
      } else {
        fareType = FlightsFareTypes.MULTI_CITY
      }

      let fareClass = flights[0].fareFamily?.cabin.toLowerCase()
      if (!fareClass || !fareClasses.includes(fareClass as typeof fareClasses[number])) {
        fareClass = 'economy'
      }

      const flightSearchResults = await searchFlights({
        currency: currentCurrency,
        region: currentRegionCode,
        occupancies: [occupants],
        itinerary: flights.map(flight => ({
          departureAirport: flight.departingAirportCode,
          arrivalAirport: flight.arrivalAirportCode,
          departureDate: flight.departingDate,
        })) || [],
        fareClass,
        fareType,
        forceBundleId,
        viewType: fareType === FlightsFareTypes.ONE_WAY ? 'OneWay' : undefined, // WHY is this necessary???
      }, { enableSabreContent })

      const { viewType, searchId } = flightSearchResults

      const fares = nonNullable(flights.map((originalFare, i) => (
        flightSearchResults.onewayFares[i].find(fare => fare.bookingInfo.journeyKey === originalFare.journeyKey)
      )))

      if (fares.length !== flights.length) {
        // Some or all flights not matched up
        multiCartRefreshFlightItemEvent(item, 'failed')
        setStatus('refreshFailed')
        return
      }

      let upsells: Array<App.FlightFareFamily | undefined> = []

      if (viewType === 'TwoOneWays') {
        let departingUpsell: App.FlightFareFamily | undefined
        let returningUpsell: App.FlightFareFamily | undefined

        if (flights[0].fareFamily) {
          const departingUpsellResults = await getFlightFareFamilies({
            searchId: searchId!,
            sessionId: '',
            origin: flights[0].departingAirportCode,
            destination: flights[0].arrivalAirportCode,
            occupancies: occupants,
            region: currentRegionCode,
            isStandalone,
            selectedFareIds: [fares[0].id],
          })
          departingUpsell = departingUpsellResults.upsellFares.find(upsellFare => upsellFare.fareBasis === flights[0].fareFamily?.fareBasis)
        }

        if (flights[1].fareFamily) {
          const returningUpsellResults = await getFlightFareFamilies({
            searchId: searchId!,
            sessionId: '',
            origin: flights[1].departingAirportCode,
            destination: flights[1].arrivalAirportCode,
            occupancies: occupants,
            region: currentRegionCode,
            departingUpsellOptionId: departingUpsell?.id,
            isStandalone,
            selectedFareIds: [fares[0].id, fares[1].id],
          })
          returningUpsell = returningUpsellResults.upsellFares.find(upsellFare => upsellFare.fareBasis === flights[1].fareFamily?.fareBasis)
        }

        upsells = [departingUpsell, returningUpsell]
      } else if (viewType === 'ReturnTwoOneWays') {
        const lastFlight = last(flights)
        const upsellResults = await getFlightFareFamilies({
          searchId: searchId!,
          sessionId: '',
          origin: lastFlight.departingAirportCode,
          destination: lastFlight.arrivalAirportCode,
          occupancies: occupants,
          region: currentRegionCode,
          isStandalone,
          selectedFareIds: fares.map(fare => fare.id),
        })

        upsells = flights.map((originalFare, i) => {
          const upsellFares = upsellResults.upsellFaresPerLeg?.[i] ?? upsellResults.upsellFares
          return upsellFares.find(upsellFare => upsellFare.fareBasis === originalFare.fareFamily?.fareBasis)
        })
      } else if (viewType === 'MultiCity' || viewType === 'OneWay') {
        upsells = await Promise.all(flights.map(async(originalFare, i) => {
          if (originalFare.fareFamily) {
            const upsellResults = await getFlightFareFamilies({
              searchId: searchId!,
              sessionId: '',
              origin: originalFare.departingAirportCode,
              destination: originalFare.arrivalAirportCode,
              occupancies: occupants,
              region: currentRegionCode,
              isStandalone,
              selectedFareIds: fares.map(fare => fare.id),
            })
            const upsellFares = upsellResults.upsellFaresPerLeg?.[i] ?? upsellResults.upsellFares
            return upsellFares.find(upsellFare => upsellFare.fareBasis === originalFare.fareFamily?.fareBasis)
          }
          return undefined
        }))
      } else {
        // Unsupported viewType
        multiCartRefreshFlightItemEvent(item, 'failed')
        setStatus('refreshFailed')
        return
      }

      const expiry = moment(flightSearchResults.timestamp).add(FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, 'minutes').toDate().getTime()

      const updatedItem: App.Checkout.FlightItem = {
        ...item,
        expiry,
        // our flight is changing, reset the fare fetching behaviour
        hasFinalFare: false,
        fareFetchError: undefined,
        journeyType: fares[0].itemType,
        forceBundleId,
        totalFare: 0,
        searchId,
        viewType: fares[0].fareType === FlightViewTypes.RETURN ? FlightViewTypes.TWO_ONE_WAYS_AND_RETURN : item.viewType,
        flights: flights.map((flight, i) => {
          const journey = fares[i]
          const journeyFields = getJourneyFields(journey)

          return {
            ...journeyFields,
            carrier: journey.flightGroup.flights[0].marketingAirline.code,
            extras: flight.extras ?? EmptyBaggage,
            fareFamily: upsells[i],
            departingAirportCode: journey.flightGroup.flights[0].departureAirport,
            arrivalAirportCode: last(journey.flightGroup.flights).arrivalAirport,
            departingDate: journey.flightGroup.flights[0].departingDate,
            arrivalAirportName: last(journey.flightGroup.flights).arrivalAirportName,
            departingAirportName: journey.flightGroup.flights[0].departureAirportName,
            arrivalDateTime: last(journey.flightGroup.flights).arrivalDateTime,
            departureDateTime: journey.flightGroup.flights[0].departureDateTime,
            duration: journey.flightGroup.totalFlightDuration,
            numberOfStops: journey.flightGroup.flights.length - 1,
          }
        }),
      }

      multiCartRefreshFlightItemEvent(item, 'success')
      dispatch(userCartItemUpdate(updatedItem))
      // Toggle save for later to reactivate item
      dispatch(userCartItemsSaveForLater([item.itemId]))
      setStatus('valid')
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // TODO: log error
      multiCartRefreshFlightItemEvent(item, 'failed')
      setStatus('refreshFailed')
      return
    }
  }, [dispatch, currentCurrency, currentRegionCode, item, enableSabreContent])

  return {
    dataStatus,
    initiateRefresh,
    refreshStartedAt,
  }
}

export default useRefreshFlightItem
